/**
 * Interactive step-by-step checklist for info-only calibration solutions.
 *
 * Only renders for the three slugs in CALIBRATION_STEPS.
 * Checkbox state is persisted on the Pi in the single
 * `stock-calibration` namespace, shaped `Record<slug, boolean[]>`.
 * The legacy per-slug localStorage keys
 * (`reefapp.stockSolutions.calibration.<slug>`) seed the default and
 * are never deleted.
 */
import { useCallback, useMemo, useRef, useState } from 'react';

import { useServerBackedState } from '../hooks/useServerBackedState';
import { CALIBRATION_STEPS } from './calibrationSteps';

/** `/api/userdata` namespace holding every checklist on the Pi. */
export const STOCK_CALIBRATION_NAMESPACE = 'stock-calibration';

/** Legacy per-slug key prefix — read as the seed, never deleted. */
const LEGACY_KEY_PREFIX = 'reefapp.stockSolutions.calibration.';

type CalibrationRecord = Record<string, boolean[]>;

interface CalibrationHelperProps {
  slug: string;
}

function isBooleanArray(value: unknown): value is boolean[] {
  return (
    Array.isArray(value) &&
    value.every((v): v is boolean => typeof v === 'boolean')
  );
}

/** Collect every legacy per-slug checklist into one record. */
function readLegacyCalibration(): CalibrationRecord {
  const record: CalibrationRecord = {};
  for (const slug of Object.keys(CALIBRATION_STEPS)) {
    try {
      const raw = window.localStorage.getItem(`${LEGACY_KEY_PREFIX}${slug}`);
      if (raw === null) continue;
      const parsed: unknown = JSON.parse(raw);
      if (isBooleanArray(parsed)) record[slug] = parsed;
    } catch {
      // corrupt entry — skip this slug
    }
  }
  return record;
}

/** Narrow the opaque server blob to a usable record. */
function asCalibrationRecord(value: unknown): CalibrationRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  const record: CalibrationRecord = {};
  for (const [slug, checked] of Object.entries(value)) {
    if (isBooleanArray(checked)) record[slug] = checked;
  }
  return record;
}

export function CalibrationHelper({ slug }: CalibrationHelperProps) {
  const steps = CALIBRATION_STEPS[slug];
  const [legacyDefault] = useState(readLegacyCalibration);
  const { value, setValue } = useServerBackedState<CalibrationRecord>(
    STOCK_CALIBRATION_NAMESPACE,
    legacyDefault,
  );
  const record = useMemo(() => asCalibrationRecord(value), [value]);
  const recordRef = useRef(record);
  recordRef.current = record;

  const checked = useMemo<boolean[]>(() => {
    if (!steps) return [];
    const stored = record[slug];
    if (stored && stored.length === steps.length) return stored;
    return steps.map(() => false);
  }, [record, slug, steps]);
  const checkedRef = useRef(checked);
  checkedRef.current = checked;

  const toggleStep = useCallback(
    (index: number) => {
      const next = [...checkedRef.current];
      next[index] = !next[index];
      setValue({ ...recordRef.current, [slug]: next });
    },
    [slug, setValue],
  );

  const handleReset = useCallback(() => {
    if (window.confirm('Reset all checkboxes?')) {
      const reset = steps ? steps.map(() => false) : [];
      setValue({ ...recordRef.current, [slug]: reset });
    }
  }, [slug, steps, setValue]);

  const completed = useMemo(
    () => checked.filter(Boolean).length,
    [checked],
  );

  if (!steps) return null;

  return (
    <div className="mt-6 bg-slate-900 p-6 rounded-xl border border-slate-700">
      <h3 className="text-xl font-semibold text-cyan-300 mb-3">
        Calibration Checklist
      </h3>
      <p className="text-sm text-slate-400 mb-3">
        {completed} / {steps.length} steps complete
      </p>
      <ul className="space-y-2">
        {steps.map((step, i) => (
          <li key={i}>
            <label
              className={`flex items-start gap-3 cursor-pointer ${
                checked[i] ? 'line-through text-slate-500' : 'text-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={checked[i]}
                onChange={() => toggleStep(i)}
                className="mt-0.5 accent-cyan-500"
              />
              <span className="text-sm">{step}</span>
            </label>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={handleReset}
        className="mt-4 text-xs text-slate-400 hover:text-red-400 transition-colors"
      >
        Reset checklist
      </button>
    </div>
  );
}
